import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { EditProgramExerciseModal } from './EditProgramExerciseModal';
import { Exercise } from '@/data/exercises';
import {
  Download, 
  Play, 
  Pause, 
  CheckCircle, 
  Clock, 
  Target, 
  Dumbbell,
  Calendar,
  X,
  Pencil,
  Trash2,
  Sparkles,
  Route,
} from 'lucide-react';
import { WorkoutProgram, WorkoutProgramDay, WorkoutProgramExercise } from '@/hooks/useWorkoutPrograms';
import { buildCoachPlanFromProgramRunDay } from '@/utils/workoutImporter';
import { exportToPDF } from '@/lib/pdfExport';
import { toast } from 'sonner';
import { Analytics } from '@/lib/analytics';
import { useTranslation } from 'react-i18next';
import { usePremium } from '@/hooks/usePremium';
import { usePremiumGate } from '@/contexts/PremiumGateContext';
import { isDateLikeLabel } from '@/lib/programDates';
import {
  isProgramDayRunning,
  prepareProgramRunLaunch,
  toastOpeningRun,
} from '@/lib/startProgramRun';

interface WorkoutProgramViewerProps {
  program: WorkoutProgram | null;
  isOpen: boolean;
  onClose: () => void;
  onStatusUpdate: (programId: string, status: string) => void;
  onDayComplete: (programId: string, dayId: string, dayData: any) => Promise<any>;
  onUpdateExercise?: (exerciseId: string, updates: Partial<WorkoutProgramExercise>) => Promise<void>;
  onDeleteExercise?: (exerciseId: string) => Promise<void>;
}

export const WorkoutProgramViewer = ({ 
  program, 
  isOpen, 
  onClose, 
  onStatusUpdate,
  onDayComplete,
  onUpdateExercise,
  onDeleteExercise
}: WorkoutProgramViewerProps) => {
  const { t } = useTranslation();
  const { isPremium } = usePremium();
  const { openPaywall } = usePremiumGate();
  const [isExporting, setIsExporting] = useState(false);
  const [completingDay, setCompletingDay] = useState<string | null>(null);
  const [editingExercise, setEditingExercise] = useState<WorkoutProgramExercise | null>(null);
  const navigate = useNavigate();

  if (!program) return null;

  const isWorkoutDayRunning = (exercises: WorkoutProgramExercise[]) =>
    isProgramDayRunning(exercises);

  const cleanLabel = (name: string) => name.replace(/\s*\(Dedicated\)/i, '').trim();

  const renderRunningDayDetails = (day: WorkoutProgramDay, compact = false) => {
    const exercise = day.exercises[0];
    const coachLines = exercise
      ? buildCoachPlanFromProgramRunDay(day, exercise)
      : [];
    const duration = day.estimated_duration_minutes;

    return (
      <div className={`bg-gradient-to-br from-primary/5 to-primary/10 rounded-lg ${compact ? 'p-4' : 'p-4'}`}>
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 bg-gradient-primary rounded-full flex items-center justify-center shrink-0">
            <Route className="h-5 w-5 text-black" />
          </div>
          <div className="min-w-0 flex-1">
            <h4 className="font-semibold text-base leading-tight">{cleanLabel(day.workout_title)}</h4>
            <p className="text-xs text-muted-foreground mt-0.5">
              {t('components.workoutProgramViewer.runAiGuideHint')}
            </p>
            {duration ? (
              <div className="flex items-center gap-1 mt-1.5 text-xs text-primary font-medium">
                <Clock className="h-3 w-3" />
                {t('components.workoutProgramViewer.runDuration', { minutes: duration })}
              </div>
            ) : null}
          </div>
        </div>

        {day.workout_description ? (
          <p className="text-sm text-muted-foreground mb-3">{day.workout_description}</p>
        ) : null}

        {coachLines.length > 0 ? (
          <div className="rounded-md bg-background/80 p-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
              {t('components.workoutProgramViewer.runCoachPhases')}
            </p>
            <ol className="space-y-1.5">
              {coachLines.map((line, idx) => (
                <li key={idx} className="flex gap-2 text-sm text-foreground">
                  <span className="text-primary font-semibold shrink-0">{idx + 1}.</span>
                  <span className="leading-snug">{line}</span>
                </li>
              ))}
            </ol>
          </div>
        ) : exercise?.notes ? (
          <p className="text-sm text-foreground whitespace-pre-wrap rounded-md bg-background/80 p-3">
            {exercise.notes}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {t('components.workoutProgramViewer.noRunningDetails')}
          </p>
        )}
      </div>
    );
  };

  const handleStartRunningFromProgram = async (day: WorkoutProgramDay) => {
    const result = await prepareProgramRunLaunch(day, { enableCoach: isPremium });
    if (!result?.planText) {
      toast.error(t('components.workoutProgramViewer.noRunningDetails'));
      return;
    }

    toastOpeningRun(t('components.workoutProgramViewer.toast.openingRunning'));
    navigate('/running-start', {
      state: {
        openCoachGuide: true,
        coachGuidePlan: result.planText,
        requirePremiumForCoach: !isPremium,
      },
    });
    onClose();
  };

  const handleStartWorkout = (day: WorkoutProgramDay) => {
    if (isWorkoutDayRunning(day.exercises)) {
      void handleStartRunningFromProgram(day);
      return;
    }

    try {
      console.log('Starting workout from program day:', day);
      console.log('Day exercises:', day.exercises);
      
      // Build exercises directly for the gym workout format
      // Bypass convertToWorkoutLoggerFormat which has strict filtering
      const exercises = (day.exercises || []).map((ex: any, index: number) => {
        // Handle both reps (single value) and reps_min/reps_max (range)
        let repsValue = 10; // Default fallback
        if (ex.reps_max !== null && ex.reps_max !== undefined) {
          repsValue = typeof ex.reps_max === 'number' ? ex.reps_max : parseInt(String(ex.reps_max), 10) || 10;
        } else if (ex.reps_min !== null && ex.reps_min !== undefined) {
          repsValue = typeof ex.reps_min === 'number' ? ex.reps_min : parseInt(String(ex.reps_min), 10) || 10;
        } else if (ex.reps !== null && ex.reps !== undefined) {
          repsValue = typeof ex.reps === 'number' ? ex.reps : parseInt(String(ex.reps), 10) || 10;
        }

        // Parse sets with fallback
        let setsValue = 3; // Default fallback
        if (ex.sets !== null && ex.sets !== undefined) {
          setsValue = typeof ex.sets === 'number' ? ex.sets : parseInt(String(ex.sets), 10) || 3;
        }
        
        // Parse weight with fallback to 0 (bodyweight)
        let weightValue = 0;
        if (ex.weight_kg !== null && ex.weight_kg !== undefined) {
          weightValue = typeof ex.weight_kg === 'number' ? ex.weight_kg : parseFloat(String(ex.weight_kg)) || 0;
        }

        const exerciseData = {
          id: `program-import-${Date.now()}-${index}`,
          name: String(ex.exercise_name || 'Unknown Exercise').trim(),
          sets: setsValue,
          reps: repsValue,
          weight: weightValue,
          muscleGroups: ex.muscle_groups || [],
          notes: ex.notes || null,
          matched: true // Mark as matched so GymWorkout handles it properly
        };
        
        console.log('Converted exercise:', exerciseData);
        return exerciseData;
      });

      console.log('Final exercises for import:', exercises);

      if (exercises.length > 0) {
        localStorage.removeItem('activeWorkoutSession');
        localStorage.setItem('importedWorkout', JSON.stringify({
          title: day.workout_title || t('components.workoutProgramViewer.programDayFallback'),
          exercises,
          imported: true,
        }));
        console.log('Saved importedWorkout to localStorage');
      } else {
        console.warn('No exercises to import from program day');
      }

      navigate('/gym-workout');
      toast.success(t('components.workoutProgramViewer.toast.openingGym'));
    } catch (e) {
      console.error('Failed to start workout from program day:', e);
      navigate('/gym-workout');
    }
    onClose();
  };

  const handleExportPDF = async () => {
    if (!isPremium) {
      toast.error(t('premium.pdfExportLocked'));
      openPaywall({ description: t('premium.pdfExportLocked'), source: 'pdf_export' });
      return;
    }
    setIsExporting(true);
    try {
      await exportToPDF(program);
      toast.success(t('components.workoutProgramViewer.toast.exportSuccess'));
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error(t('components.workoutProgramViewer.toast.exportError'));
    } finally {
      setIsExporting(false);
    }
  };

  const handleStatusUpdate = async (status: string) => {
    try {
      await onStatusUpdate(program.id, status);
      toast.success(t('components.workoutProgramViewer.toast.statusUpdated', { status }));
    } catch (error) {
      toast.error(t('components.workoutProgramViewer.toast.statusUpdateError'));
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-500/10 text-green-500 border-green-500/20';
      case 'completed': return 'bg-blue-500/10 text-blue-500 border-blue-500/20';
      case 'paused': return 'bg-yellow-500/10 text-yellow-500 border-yellow-500/20';
      default: return 'bg-muted/10 text-muted-foreground border-border';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active': return <Play className="h-3 w-3" />;
      case 'completed': return <CheckCircle className="h-3 w-3" />;
      case 'paused': return <Pause className="h-3 w-3" />;
      default: return <Clock className="h-3 w-3" />;
    }
  };

  const handleDayComplete = async (dayId: string, dayData: any) => {
    setCompletingDay(dayId);
    try {
      await onDayComplete(program.id, dayId, dayData);
      toast.success(t('components.workoutProgramViewer.toast.dayCompleted'));
    } catch (error) {
      toast.error(t('components.workoutProgramViewer.toast.dayCompleteError'));
    } finally {
      setCompletingDay(null);
    }
  };

  const getCompletedCount = () => {
    return program.days.filter(d => d.completed_at).length;
  };

  const getProgressPercentage = () => {
    return Math.round((getCompletedCount() / program.days.length) * 100);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-[420px] sm:max-w-3xl max-h-[90vh] p-0 mx-auto flex flex-col overflow-hidden">
        <div
          data-program-scroll
          className="program-scroll-touch flex-1 min-h-0 overflow-y-auto overflow-x-hidden"
          style={{
            WebkitOverflowScrolling: 'touch',
            touchAction: 'pan-y',
            overscrollBehavior: 'contain',
          }}
        >
          <div className="p-6 pb-4">
            <DialogHeader className="pb-0">
              <div className="flex items-center justify-between pr-12">
                <div className="flex-1">
                  <DialogTitle className="text-2xl font-bold mb-2">
                    {program.title}
                  </DialogTitle>
                  <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {program.duration_weeks} {t('components.workoutProgramViewer.weeksUnit')}
                    </div>
                    <div className="flex items-center gap-1">
                      <Target className="h-4 w-4" />
                      {t('components.workoutProgramViewer.completedFraction', {
                        done: getCompletedCount(),
                        total: program.days.length,
                        percent: getProgressPercentage(),
                      })}
                    </div>
                    <Badge className={getStatusColor(program.status)}>
                      {getStatusIcon(program.status)}
                      {program.status}
                    </Badge>
                  </div>
                </div>
              </div>
            </DialogHeader>

            <div className="mt-4">
              <div className="flex flex-wrap justify-center sm:justify-start gap-2 mb-4">
                <Button
                  onClick={handleExportPDF}
                  disabled={isExporting}
                  className="bg-gradient-primary text-black text-sm"
                  size="sm"
                >
                  <Download className="h-3 w-3 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{isExporting ? t('components.workoutProgramViewer.exporting') : t('components.workoutProgramViewer.exportPdf')}</span>
                  <span className="sm:hidden">{t('components.workoutProgramViewer.exportPdfShort')}</span>
                </Button>

                {program.status !== 'active' && (
                  <Button
                  onClick={() => handleStatusUpdate('active')}
                  variant="outline"
                  size="sm"
                  className="text-sm"
                >
                  <Play className="h-3 w-3 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{t('components.workoutProgramViewer.startProgram')}</span>
                  <span className="sm:hidden">{t('components.workoutProgramViewer.startShort')}</span>
                </Button>
                )}

                {program.status === 'active' && (
                  <Button
                    onClick={() => handleStatusUpdate('paused')}
                    variant="outline"
                    size="sm"
                    className="text-sm"
                  >
                    <Pause className="h-3 w-3 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('components.workoutProgramViewer.pauseProgram')}</span>
                    <span className="sm:hidden">{t('components.workoutProgramViewer.pauseShort')}</span>
                  </Button>
                )}

                {(program.status === 'active' || program.status === 'paused') && (
                  <Button
                    onClick={() => handleStatusUpdate('completed')}
                    variant="outline"
                    size="sm"
                    className="text-sm"
                  >
                    <CheckCircle className="h-3 w-3 mr-1 sm:mr-2" />
                    <span className="hidden sm:inline">{t('components.workoutProgramViewer.markComplete')}</span>
                    <span className="sm:hidden">{t('components.workoutProgramViewer.completeShort')}</span>
                  </Button>
                )}
              </div>

              {program.description && (
                <Card className="mb-4">
                  <CardContent className="pt-4">
                    <p className="text-muted-foreground">{program.description}</p>
                  </CardContent>
                </Card>
              )}
            </div>

            <Tabs defaultValue="overview" className="mt-4">
              <div className="px-4 sm:px-6">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="overview" className="text-xs sm:text-sm">{t('components.workoutProgramViewer.tabOverview')}</TabsTrigger>
                  <TabsTrigger value="detailed" className="text-xs sm:text-sm">{t('components.workoutProgramViewer.tabDetailed')}</TabsTrigger>
                </TabsList>
              </div>

              <TabsContent value="overview" className="px-4 sm:px-6 mt-4">
                <div className="grid gap-4 pb-6">
                {program.days.map((day) => (
                  <Card key={day.id} className={`border-l-4 ${day.completed_at ? 'border-l-green-500 bg-green-500/5' : 'border-l-primary'}`}>
                    <CardHeader className="pb-3">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <CardTitle className="text-base sm:text-lg flex items-center gap-1.5">
                            {isDateLikeLabel(day.day_name) && <Calendar className="h-3.5 w-3.5 text-primary shrink-0" />}
                            {day.day_name || t('components.workoutProgramViewer.dayLabel', { number: day.day_number })}: {cleanLabel(day.workout_title)}
                          </CardTitle>
                          {day.completed_at && (
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              {t('components.workoutProgramViewer.doneBadge')}
                            </Badge>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="h-3 w-3" />
                          {day.estimated_duration_minutes || 45} {t('components.workoutProgramViewer.minSuffix')}
                        </div>
                      </div>
                      {day.workout_description && (
                        <p className="text-sm text-muted-foreground">
                          {day.workout_description}
                        </p>
                      )}
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex flex-col gap-3">
                        {isWorkoutDayRunning(day.exercises) ? (
                          renderRunningDayDetails(day, true)
                        ) : (
                          <div className="space-y-1">
                            {day.exercises.map((exercise) => (
                              <div key={exercise.id} className="flex items-center gap-1 group">
                                <Badge variant="secondary" className="text-xs flex-1 justify-start">
                                  <Dumbbell className="h-3 w-3 mr-1 shrink-0" />
                                  <span className="truncate">{cleanLabel(exercise.exercise_name)}</span>
                                  {exercise.sets && exercise.reps_max && (
                                    <span className="ml-1 text-muted-foreground">
                                      {exercise.sets}×{exercise.reps_min === exercise.reps_max ? exercise.reps_max : `${exercise.reps_min}-${exercise.reps_max}`}
                                    </span>
                                  )}
                                </Badge>
                                {onUpdateExercise && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0"
                                    onClick={() => setEditingExercise(exercise)}
                                  >
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                )}
                                {onDeleteExercise && day.exercises.length > 1 && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 text-destructive hover:text-destructive"
                                    onClick={async () => {
                                      if (window.confirm(t('components.workoutProgramViewer.removeExerciseConfirm', { name: exercise.exercise_name }))) {
                                        try {
                                          await onDeleteExercise(exercise.id);
                                          toast.success(t('components.workoutProgramViewer.toast.exerciseRemoved'));
                                        } catch {
                                          toast.error(t('components.workoutProgramViewer.toast.exerciseRemoveError'));
                                        }
                                      }
                                    }}
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        <div className="flex flex-col gap-2">
                          <Button 
                            size="sm" 
                            onClick={() => handleStartWorkout(day)}
                            className="bg-gradient-primary text-black w-full"
                            disabled={!!day.completed_at}
                          >
                            {isWorkoutDayRunning(day.exercises) ? (
                              <>
                                <Sparkles className="h-3.5 w-3.5 mr-2" />
                                {t('components.workoutProgramViewer.startAiGuidedRun')}
                              </>
                            ) : (
                              <>
                                <Play className="h-3 w-3 mr-2" />
                                {t('components.workoutProgramViewer.startWorkout')}
                              </>
                            )}
                          </Button>
                          {!day.completed_at && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDayComplete(day.id, day)}
                              disabled={completingDay === day.id}
                              className="border-green-500/60 text-green-500 hover:bg-green-500/10 w-full"
                            >
                              <CheckCircle className="h-3 w-3 mr-2" />
                              {completingDay === day.id ? t('components.workoutProgramViewer.marking') : t('components.workoutProgramViewer.markDone')}
                            </Button>
                          )}
                        </div>

                      </div>
                    </CardContent>
                  </Card>
                ))}
                </div>
              </TabsContent>

              <TabsContent value="detailed" className="px-4 sm:px-6 mt-4">
                <div className="space-y-6 pb-6">
                {program.days.map((day, dayIndex) => (
                  <div key={day.id}>
                    <div className="flex items-center gap-3 mb-4">
                      <div className={`w-8 h-8 ${day.completed_at ? 'bg-green-500 text-white' : 'bg-gradient-primary text-black'} rounded-full flex items-center justify-center font-bold text-sm`}>
                        {day.completed_at ? <CheckCircle className="h-5 w-5" /> : day.day_number}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-lg flex items-center gap-1.5">
                            {isDateLikeLabel(day.day_name) && <Calendar className="h-4 w-4 text-primary shrink-0" />}
                            {day.day_name || t('components.workoutProgramViewer.dayLabel', { number: day.day_number })}
                          </h3>
                          {day.completed_at && (
                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
                              {t('components.workoutProgramViewer.completedBadge')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-muted-foreground">{cleanLabel(day.workout_title)}</p>
                      </div>
                    </div>

                    <div className="ml-11 space-y-3">
                      {isWorkoutDayRunning(day.exercises) ? (
                        <Card className="p-0 border-primary/20 overflow-hidden">
                          {renderRunningDayDetails(day)}
                          <div className="p-4 pt-0 flex flex-col gap-2">
                            <Button 
                              onClick={() => handleStartWorkout(day)}
                              className="bg-gradient-primary text-black w-full"
                              disabled={!!day.completed_at}
                            >
                              <Sparkles className="h-4 w-4 mr-2" />
                              {t('components.workoutProgramViewer.startAiGuidedRun')}
                            </Button>
                            {!day.completed_at && (
                              <Button 
                                variant="outline"
                                onClick={() => handleDayComplete(day.id, day)}
                                disabled={completingDay === day.id}
                                className="border-green-500/60 text-green-500 hover:bg-green-500/10 w-full"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                {completingDay === day.id ? t('components.workoutProgramViewer.marking') : t('components.workoutProgramViewer.doneShort')}
                              </Button>
                            )}
                          </div>
                        </Card>
                      ) : (
                        <>
                          {day.exercises.map((exercise, index) => (
                            <Card key={exercise.id} className="p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h4 className="font-medium">{cleanLabel(exercise.exercise_name)}</h4>
                                <div className="flex items-center gap-1">
                                  {onUpdateExercise && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() => setEditingExercise(exercise)}
                                    >
                                      <Pencil className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  {onDeleteExercise && day.exercises.length > 1 && (
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-7 w-7 text-destructive hover:text-destructive"
                                      onClick={async () => {
                                        if (window.confirm(t('components.workoutProgramViewer.removeExerciseFromDayConfirm', { name: exercise.exercise_name }))) {
                                          try {
                                            await onDeleteExercise(exercise.id);
                                            toast.success(t('components.workoutProgramViewer.toast.exerciseRemoved'));
                                          } catch {
                                            toast.error(t('components.workoutProgramViewer.toast.exerciseRemoveErrorLong'));
                                          }
                                        }
                                      }}
                                    >
                                      <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                  )}
                                  <Badge variant="outline" className="text-xs ml-1">
                                    {t('components.workoutProgramViewer.exerciseLabel', { number: index + 1 })}
                                  </Badge>
                                </div>
                              </div>
                              
                              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4 text-sm">
                                {exercise.sets && (
                                  <div>
                                    <span className="text-muted-foreground">{t('components.workoutProgramViewer.setsLabel')}</span>
                                    <p className="font-medium">{exercise.sets}</p>
                                  </div>
                                )}
                                {(exercise.reps_min || exercise.reps_max) && (
                                  <div>
                                    <span className="text-muted-foreground">{t('components.workoutProgramViewer.repsLabel')}</span>
                                    <p className="font-medium">
                                      {exercise.reps_min === exercise.reps_max 
                                        ? exercise.reps_min 
                                        : `${exercise.reps_min}-${exercise.reps_max}`
                                      }
                                    </p>
                                  </div>
                                )}
                                {exercise.weight_kg && (
                                  <div>
                                    <span className="text-muted-foreground">{t('components.workoutProgramViewer.weightLabel')}</span>
                                    <p className="font-medium">{exercise.weight_kg}kg</p>
                                  </div>
                                )}
                                {exercise.rest_seconds && (
                                  <div>
                                    <span className="text-muted-foreground">{t('components.workoutProgramViewer.restLabel')}</span>
                                    <p className="font-medium">{exercise.rest_seconds}s</p>
                                  </div>
                                )}
                              </div>

                              {exercise.muscle_groups && exercise.muscle_groups.length > 0 && (
                                <div className="mt-3">
                                  <div className="flex flex-wrap gap-1">
                                    {exercise.muscle_groups.map((muscle) => (
                                      <Badge key={muscle} variant="outline" className="text-xs">
                                        {muscle}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {exercise.notes && (
                                <div className="mt-3 p-2 bg-muted/50 rounded text-sm">
                                  <span className="text-muted-foreground">{t('components.workoutProgramViewer.notesLabel')}</span>
                                  {exercise.notes}
                                </div>
                              )}
                            </Card>
                          ))}
                          <div className="mt-2 flex gap-2">
                            <Button 
                              onClick={() => handleStartWorkout(day)}
                              className="bg-gradient-primary text-black flex-1"
                              disabled={!!day.completed_at}
                            >
                              <Play className="h-4 w-4 mr-2" />
                              {t('components.workoutProgramViewer.startGymWorkout')}
                            </Button>
                            {!day.completed_at && (
                              <Button 
                                variant="outline"
                                onClick={() => handleDayComplete(day.id, day)}
                                disabled={completingDay === day.id}
                                className="border-green-500 text-green-500 hover:bg-green-500/10"
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                {completingDay === day.id ? t('components.workoutProgramViewer.marking') : t('components.workoutProgramViewer.doneShort')}
                              </Button>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    {dayIndex < program.days.length - 1 && (
                      <Separator className="my-6" />
                    )}
                  </div>
                ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </DialogContent>

      {onUpdateExercise && (
        <EditProgramExerciseModal
          exercise={editingExercise}
          isOpen={!!editingExercise}
          onClose={() => setEditingExercise(null)}
          onSave={async (exerciseId, updates) => {
            await onUpdateExercise(exerciseId, updates as any);
            toast.success(t('components.workoutProgramViewer.toast.exerciseUpdated'));
          }}
          onReplace={async (exerciseId, newEx, keepSetsReps) => {
            const currentEx = editingExercise;
            await onUpdateExercise(exerciseId, {
              exercise_name: newEx.name,
              muscle_groups: newEx.muscle_groups,
              ...(keepSetsReps && currentEx ? {
                sets: currentEx.sets,
                reps_min: currentEx.reps_min,
                reps_max: currentEx.reps_max,
                weight_kg: currentEx.weight_kg,
                rest_seconds: currentEx.rest_seconds,
              } : {}),
            } as any);
            toast.success(t('components.workoutProgramViewer.toast.exerciseReplaced', { name: newEx.name }));
          }}
        />
      )}
    </Dialog>
  );
};
